import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import type { BBox } from "../pixel/brush";
import type { Command } from "./Command";

// A complete paint/erase stroke captured as a before/after pixel patch
// over a single bounding box. Constructed by the tool after the stroke
// ends; the live mutations during the drag are already in the buffer.

export class PaintStrokeCommand implements Command {
  readonly kind = "paint-stroke";
  constructor(
    private readonly layerId: string,
    private readonly bbox: BBox,
    private readonly before: Uint8ClampedArray,
    private readonly after: Uint8ClampedArray,
  ) {}

  apply(doc: Document): Document {
    this.writePatch(doc, this.after);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    return new PaintStrokeCommand(this.layerId, this.bbox, this.after, this.before);
  }

  private writePatch(doc: Document, patch: Uint8ClampedArray): void {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return;
    const { x, y, w, h } = this.bbox;
    const data = layer.pixels.data;
    const stride = layer.pixels.width * 4;
    for (let row = 0; row < h; row++) {
      const dstOff = ((y + row) * layer.pixels.width + x) * 4;
      const srcOff = row * w * 4;
      data.set(patch.subarray(srcOff, srcOff + w * 4), dstOff);
    }
    layer.pixels.markDirty();
    void stride;
  }
}

export function snapshotRegion(layer: RasterLayer, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  const data = layer.pixels.data;
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * layer.pixels.width + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(data.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

// Match background layers too, not just raster. A Photoshop Background is a
// paintable pixel layer (kind: "background"); paint/erase strokes on it must be
// undoable/redoable. Without this, writePatch() no-oped on the Background — the
// stroke painted live during the drag but apply()/invert() could never restore
// it, so undo/redo silently did nothing on the Background. Mirrors the shared
// LayerUtils.findRaster. (audit F2 — companion to F1 which enabled Background
// painting and exposed this.)
function findRaster(layers: readonly Layer[], id: string): RasterLayer | null {
  for (const l of layers) {
    if (l.id === id && (l.kind === "raster" || l.kind === "background")) return l as RasterLayer;
    if (l.kind === "group") {
      const inner = findRaster(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}
